/**
 * AdminCreateMenu — the "+ Create" button in the AdminLayout sidebar.
 *
 * Click → dropdown popover to the right of the button listing every
 * creatable entity grouped by Cricket / Marketplace. Each row is a
 * direct navigation — no intermediate page.
 *
 * Why a dropdown vs the earlier /admin/create tile page: the founder
 * wanted ONE button that surfaces the options directly; tile-grid
 * landing was an extra click. The /admin/create route still exists
 * as a fallback bookmark, but the button drives the primary UX.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, ChevronRight,
  Users as UsersIcon, Shield, MapPin, UserCheck,
  Trophy, Calendar, Layers, Store, Coins, Upload,
} from "lucide-react";

const CRICKET = [
  { label: "Player",      to: "/cricket/players/add",       icon: UsersIcon },
  { label: "Team",        to: "/cricket/teams/add",         icon: Shield },
  { label: "Stadium",     to: "/cricket/stadiums/add",      icon: MapPin },
  { label: "Umpire",      to: "/cricket/umpires/add",       icon: UserCheck },
  { label: "Match",       to: "/cricket/matches/add",       icon: Calendar },
  { label: "Tournament",  to: "/cricket/tournaments/add",   icon: Trophy },
  { label: "Competition", to: "/cricket/competitions/add",  icon: Layers },
];

const MARKETPLACE = [
  { label: "Pack",
    to: "/admin/marketplace?action=new",
    icon: Store },
  // 2026-08-03 — MATCH_BUNDLE creation removed from the console
  // (founder ask); the menu item deep-linked to a wizard that no
  // longer exists.
  { label: "Squad/Fixture bundle",
    to: "/admin/marketplace?action=new&type=SQUAD_PACK",
    icon: UsersIcon, badge: "NEW" },
  // TopupPack is a wallet model (apps/wallet/models.py), not a marketplace
  // Pack — there's no SPA wizard. Route to Django admin until/unless we
  // build one. Marked external so the menu opens it in a new tab.
  { label: "Topup pack",
    to: "https://api.zencricketdata.com/admin/wallet/topuppack/add/",
    icon: Coins, external: true },
];

const INGEST = [
  { label: "New ingest job",
    to: "/ingest/new",
    icon: Upload },
];

export default function AdminCreateMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Click-outside + Esc close. Mouse-down (not click) so the same press
  // that triggered "close" doesn't immediately re-open on the toggle.
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center justify-between gap-1.5 w-full px-3 py-2 rounded-md text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors shadow-sm"
      >
        <span className="flex items-center gap-1.5">
          <Plus className="w-4 h-4" />
          Create
        </span>
        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-full top-0 ml-2 w-64 z-50 rounded-lg border border-border-default bg-bg-primary shadow-xl py-1.5"
        >
          <Group label="Cricket entities">
            {CRICKET.map((item) => <Item key={item.label} item={item} onPick={() => setOpen(false)} />)}
          </Group>
          <div className="h-px bg-border-default my-1.5" />
          <Group label="Marketplace">
            {MARKETPLACE.map((item) => <Item key={item.label} item={item} onPick={() => setOpen(false)} />)}
          </Group>
          <div className="h-px bg-border-default my-1.5" />
          <Group label="Data ingest">
            {INGEST.map((item) => <Item key={item.label} item={item} onPick={() => setOpen(false)} />)}
          </Group>
        </div>
      )}
    </div>
  );
}

function Group({ label, children }) {
  return (
    <div>
      <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-text-secondary">
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Item({ item, onPick }) {
  const Icon = item.icon;
  const inner = (
    <>
      <Icon className="w-4 h-4 text-text-secondary shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge && (
        <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/40">
          {item.badge}
        </span>
      )}
    </>
  );
  const cls = "flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-bg-secondary transition-colors";
  if (item.external) {
    return (
      <a
        href={item.to}
        target="_blank"
        rel="noreferrer"
        onClick={onPick}
        role="menuitem"
        className={cls}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link
      to={item.to}
      onClick={onPick}
      role="menuitem"
      className={cls}
    >
      {inner}
    </Link>
  );
}
